import { AppointmentID } from "./Appointment";
import { BrandGroup } from "./Brand";
import { CustomerCommunicationID } from "./CustomerCommunication";
import { CommunicationEventID } from "./CustomerCommunicationEvent";
import { DealerID } from "./Dealer";
import { NotifierKey } from "./DealerLocation";
import { DeskCommunicationID } from "./DeskCheckin";
import { KeyLockerCommunicationID } from "./KeylockerCommunication";
import { KioskCommunicationID } from "./KioskCommunication";
import { LEF_LEAD_KINDS } from "./Lef";
import { TagID } from "./Tag";

export type CheckInResultID = number;

export type CheckInRemarkID = number;

export type CheckInAttachmentID = number;

export type CheckInServiceID = number;

export type CommunicationAgreementID = number;

export type CustomerCommunicationAgreementID = number;

export enum CHECKINRESULT_STATUS {
  NotSent = 0,
  Pending = 1,
  Success = 2,
  Failed = 3
}

export interface CheckInResult {
  created_on: string;
  updated_on: string;
  id: CheckInResultID; // Identifier of the checkin result
  customer_communication_id: CustomerCommunicationID; // Customer communication identifier
  check_in_service_id: CheckInServiceID | null; // Linked check in service identifier
  accepted: boolean; // Is accepted by the customer
  receptionist_handled: boolean; // Has receptionist handled this result
  tag_id: TagID | null; // Identifier of the tag if any associated with this result
  notification_email: string; // Notification email id
  status: CHECKINRESULT_STATUS; // Status of the checkin result
  reason: string; // Reason
  mailgun_message_id: string; // Mailgun message id
  name: string; // Name
  description: string; // Description
  price: number; // Price
  info_url: string; // Info URL
  hide_for_lease_appointments: boolean; // Hide for the lease appointment
  visible: boolean; // Is it visible
  can_convert_to_intervention: boolean; // Can be converted to intervention
  sent_to_lef: boolean; // Is this converted to lead in LEF
  sent_to_lef_at: string | null; // Represents the date and time at which this result has been converted to lead in LEF
  lef_lead_kind: LEF_LEAD_KINDS | null; // LEF lead kind, used while converting to lead in LEF
  appointment_id?: AppointmentID; // Appointment identifier
  notifier_key?: NotifierKey; // Notifier key
}

export interface CheckInRemark {
  created_on: string;
  updated_on: string;
  id: CheckInRemarkID;
  receptionist_handled: boolean; // Has receptionist handled this remark
  customer_communication_id: CustomerCommunicationID; // Customer communication identifier
  title: string; // Title
  description: string; // Description
  customer_name: string; // Customer name
  visible_in_dashboard: boolean; // Is visible in dashboard
  visible_to_mechanic: boolean; // Is visible to mechanic
  attachments?: CheckInAttachment[]; // Attachments
  appointment_id?: AppointmentID; // Appointment identifier
}

export interface CheckInAttachment {
  created_on: string;
  updated_on: string;
  id: CheckInAttachmentID;
  url: string;
  name: string;
  customer_communication_id: CustomerCommunicationID;
  check_in_remarks_id: CheckInRemarkID | null;
}

export interface CheckInService {
  created_on: string;
  updated_on: string;
  id: CheckInServiceID; // Identifier of the checkin service
  dealer_id: DealerID; // Identifier of the dealer
  name: string; // Name of the service
  description: string; // Description of the service
  price: number; // Price of the service
  info_url: string; // Info URL
  hide_for_lease_appointments: boolean; // If true, hide the service for lease appointment
  tag_id: TagID | null; // Identifier of the associated tag, if any
  can_convert_to_intervention: boolean; // Can be converted to intervention if true
  brand_groups?: BrandGroup[]; // List of brand groups
  notification_email?: string; // Notification email id
  send_to_lef?: boolean; // This result of this service will be send to LEF as a lead
  lef_lead_kind?: LEF_LEAD_KINDS; // LEF Lead type for the lead in the LEF
}

export interface CommunicationAgreement {
  created_on: string;
  updated_on: string;
  id: CommunicationAgreementID;
  name: string;
  text: string;
  dealer_id: DealerID;
  parent_id: CommunicationAgreementID | null;
  deleted: boolean;
  optional_online_check_in: boolean;
  optional_kiosk_check_in: boolean;
  optional_keylocker_check_in: boolean;
  optional_desk_check_in: boolean;
  optional_desk_check_out: boolean;
  optional_customcom: boolean;
  visible_online_check_in: boolean;
  visible_kiosk_check_in: boolean;
  visible_keylocker_check_in: boolean;
  visible_desk_check_in: boolean;
  visible_desk_check_out: boolean;
  visible_customcom: boolean;
  accepted?: boolean;
  customer_communication_agreement_id?: CustomerCommunicationAgreementID;
  communication_event_id?: CommunicationEventID;
  desk_communication_id?: DeskCommunicationID;
  keylocker_communication_id?: KeyLockerCommunicationID;
  kiosk_communication_id?: KioskCommunicationID;
  customer_communication_id?: CustomerCommunicationID;
  appointment_id?: AppointmentID;
}
